/**
 * Which county a case is in, for the cases list (Raquel, 14 Sep 2026).
 *
 * > *"The dashboard should show which county we're viewing. Right now... you have to download the
 * > tickets just to see which county a case belongs to."*
 *
 * ## Why this is not just `Ticket_County`
 *
 * Measured on production 15 Sep: **965 of 6,089 live cases carry their own county, 15.8%.** A
 * column reading that field alone would be blank on more than four rows in five, which is the
 * complaint rather than the fix.
 *
 * The court knows. **2,215 of the 2,229 cases linked to a court have a county on that court**, so
 * falling back to it takes coverage to **2,848 cases, 46.8%** — confirmed by the database, not by
 * addition. One fallback, roughly triple the rows filled in.
 *
 * ⚠️ **It is still blank on about half**, and that is not a bug here. Only 2,229 of 6,089 cases are
 * linked to a court at all, so for the other 3,860 the system genuinely does not know. The case
 * carries no court name as text either, only `Ticket_City`, so there is nothing else to fall back
 * to. Showing "Not recorded" is the honest answer; inventing one from the city would be a guess
 * printed as a fact.
 *
 * Pure — no components, no API. Tested by `npm run test-case-county` in the backend.
 */

/** Only the fields the rule reads, so the tests need no DTO. */
export interface CaseCountySource {
  ticket: { Ticket_County?: string | null };
  court?: { Court_County?: string | null } | null;
}

function trimmed(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  return text === '' ? null : text;
}

/**
 * The county recorded on the case, else the one on its court, else null.
 *
 * The case wins when both are set. It is the more specific record and it is what a person typed
 * about this citation, whereas the court's county describes the court.
 */
export function caseCounty(item: CaseCountySource): string | null {
  return trimmed(item.ticket.Ticket_County) ?? trimmed(item.court?.Court_County);
}
